import { Listbox, Transition } from '@headlessui/react';
import CheckOutline from 'assets/icons/CheckOutline';
import Down from 'assets/icons/Down';
import HandleX from 'assets/icons/HandleX';
import LoadingThree from 'assets/icons/LoadingThree';
import MoreDots from 'assets/icons/MoreDots';
import Pause from 'assets/icons/Pause';
import Resume from 'assets/icons/Resume';
import Up from 'assets/icons/Up';
import Chip from 'common/components/Chip';
import InlineLoader from 'common/components/InlineLoader';
import useTranslation from 'common/hooks/useTranslation';
import { cn } from 'common/utils';
import React, { Fragment } from 'react';

const listboxStyles = {
  list: () =>
    cn(
      'mt-1 w-full rounded-md bg-white text-base shadow-lg',
      'ring-1 ring-black ring-opacity-5 focus:outline-none',
      'max-h-[180px] absolute cursor-pointer',
      'overflow-y-auto scrollbar scrollbar-w-1',
      'scrollbar-thumb-vibrant-orange scrollbar-thumb-rounded',
      'scrollbar-track-rounded z-50',
    ),
  option: ({ active }) =>
    cn(
      'flex w-full items-center',
      'px-4 py-2 text-sm gap-2.5',
      'select-none truncate text-black',
      {
        'bg-vibrant-orange bg-opacity-10': active,
      },
    ),
};

function classNames(...classes) {
  return classes.filter(Boolean).join(' ');
}

const STATE = {
  DRAFT: 'DRAFT',
  IN_PROGRESS: 'IN_PROGRESS',
  DONE: 'DONE',
  CANCELED: 'CANCELED',
  PAUSED: 'PAUSED',
};

const TRANSITION = {
  START: 'start',
  RESUME: 'resume',
  CLOSE: 'close',
  PAUSE: 'pause',
  CANCEL: 'cancel',
};

// Map for transitions
const OfferStatusTransitions = [
  {
    id: TRANSITION.START,
    icon: (iconSize = 24) => <Resume size={iconSize} color='#7A7A7A' />,
    nextState: STATE.IN_PROGRESS,
  },
  {
    id: TRANSITION.RESUME,
    icon: (iconSize = 24) => <Resume size={iconSize} color='#7A7A7A' />,
    nextState: STATE.IN_PROGRESS,
  },
  {
    id: TRANSITION.CLOSE,
    icon: (iconSize = 12) => (
      <CheckOutline strokeColor='#7A7A7A' size={iconSize} />
    ),
    nextState: STATE.DONE,
  },
  {
    id: TRANSITION.PAUSE,
    icon: (iconSize = 24) => <Pause size={iconSize} strokeColor='#7A7A7A' />,
    nextState: STATE.PAUSED,
  },
  {
    id: TRANSITION.CANCEL,
    icon: (iconSize = 12) => <HandleX size={iconSize} strokeColor='#7A7A7A' />,
    nextState: STATE.CANCELED,
  },
];

const OfferStatusStates = [
  {
    id: STATE.DRAFT,
    transitions: [TRANSITION.START],
    cssVariant: 'grey-outlined',
    icon: (iconSize = 12) => <MoreDots size={iconSize} />,
  },
  {
    id: STATE.IN_PROGRESS,
    transitions: [TRANSITION.PAUSE, TRANSITION.CLOSE, TRANSITION.CANCEL],
    cssVariant: 'alt-secondary-outlined',
    icon: (iconSize = 12) => <LoadingThree size={iconSize} />,
  },
  {
    id: STATE.DONE,
    transitions: [TRANSITION.RESUME],
    cssVariant: 'alt-success-outlined',
    icon: (iconSize = 12) => (
      <CheckOutline strokeColor={'#59BC7D'} size={iconSize} />
    ),
  },
  {
    id: STATE.CANCELED,
    transitions: [TRANSITION.RESUME],
    cssVariant: 'alert-outlined',
    icon: (iconSize = 12) => <HandleX size={iconSize} />,
  },
  {
    id: STATE.PAUSED,
    transitions: [TRANSITION.RESUME],
    cssVariant: 'alt-warning-outlined',
    icon: (iconSize = 12) => <Pause size={iconSize} strokeColor={'#DD9200'} />,
  },
];

const findStatusState = (statusId) =>
  OfferStatusStates.find(({ id }) => id === statusId);
const findStatusTransition = (transitionId) =>
  OfferStatusTransitions.find(({ id }) => id === transitionId);

const StatusCombo = ({
  currentStatus,
  handleUpdateOfferStatus,
  isUpdatingOfferStatus,
  chipClassName = '',
}) => {
  const { t } = useTranslation('offers\\details');

  const selectedStatusState = findStatusState(currentStatus);

  return (
    <div className='relative inline-block'>
      <div className='flex flex-col'>
        <Listbox value={currentStatus} disabled={isUpdatingOfferStatus}>
          {({ open }) => (
            <>
              <div className='relative'>
                <Listbox.Button>
                  <Chip
                    className={cn(
                      'uppercase px-4 py-1.5 cursor-pointer text-sm ',
                      isUpdatingOfferStatus && 'cursor-not-allowed',
                      chipClassName,
                    )}
                    variant={selectedStatusState?.cssVariant}
                  >
                    <div
                      className={cn(
                        'flex items-center gap-1',
                        isUpdatingOfferStatus && 'opacity-50',
                      )}
                    >
                      {selectedStatusState?.icon(16)}
                      <p className='truncate'>
                        {t(
                          `statusNames.${selectedStatusState?.id.toLowerCase()}`,
                        )}
                      </p>
                    </div>
                    {isUpdatingOfferStatus ? (
                      <InlineLoader size={16} />
                    ) : open ? (
                      <Up size={16} />
                    ) : (
                      <Down size={16} />
                    )}
                  </Chip>
                </Listbox.Button>

                <Transition
                  show={open}
                  as={Fragment}
                  leave='transition ease-in duration-100'
                  leaveFrom='opacity-100'
                  leaveTo='opacity-0'
                >
                  <Listbox.Options className={listboxStyles.list()}>
                    {selectedStatusState?.transitions.map((transition) => {
                      const statusTransition = findStatusTransition(transition);
                      return (
                        <Listbox.Option
                          key={statusTransition.id}
                          className={listboxStyles.option}
                          value={statusTransition.id}
                          onClick={() =>
                            handleUpdateOfferStatus(statusTransition.nextState)
                          }
                        >
                          {({ selected, active }) => (
                            <>
                              <div className='flex items-center'>
                                {statusTransition.icon(16)}
                                <span
                                  className={classNames(
                                    selected
                                      ? 'font-semibold text-dark-charcoal'
                                      : 'font-normal',
                                    'ml-3 block truncate',
                                  )}
                                >
                                  {t(
                                    `statusTransitions.${statusTransition.id.toLowerCase()}`,
                                  )}
                                </span>
                              </div>
                            </>
                          )}
                        </Listbox.Option>
                      );
                    })}
                  </Listbox.Options>
                </Transition>
              </div>
            </>
          )}
        </Listbox>
      </div>
    </div>
  );
};

export { StatusCombo, findStatusState, STATE };
